import { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtISO = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

const daysFromNow = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
};

const COVERAGE_BADGE = {
  Covered: "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered": "text-danger-500 font-medium",
};

const HEADERS = [
  "checkbox",
  "Code",
  "Linked Payments",
  "Client",
  "Payment Coverage",
  "Total",
  "Write Off Amount",
  "Paid Amount",
  "Due Amount",
  "Currency",
  "Issued At",
  "Last Sent At",
  "Due At",
  "Action",
];

const COL_WIDTHS = [
  "2%",  // checkbox
  "5%",  // code
  "7%",  // linked payments
  "10%", // coverage
  "10%", // client
  "6%",  // total
  "7%",  // write off
  "6%",  // paid
  "6%",  // due
  "5%",  // currency
  "8%",  // issued at
  "8%",  // last sent at
  "10%", // due at
  "5%",  // actions
];

const ClientInvoicesPage = () => {
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.CLIENT_INVOICES);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 50 });
  const [exporting, setExporting] = useState(false);

  const fetchInvoices = async (page = 1) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/project-finances/client-invoices", {
        params: { page, limit: 50 },
        withCredentials: true,
      });
      setInvoices(res.data.data || []);
      setMeta(res.data.meta || { total: 0, page: 1, limit: 50 });
    } catch (err) {
      console.error(err);
      setError("Failed to fetch invoices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Nothing left to pay: paid in full or written off. A part-paid invoice can still be covered.
  const isPaid = (inv) => parseFloat(inv.due_amount || 0) <= 0;
  const isFullyWrittenOff = (inv) =>
    parseFloat(inv.write_off_amount || 0) > 0 &&
    parseFloat(inv.write_off_amount) >= parseFloat(inv.total || 0);

  const handleToggle = (id) => {
    const inv = invoices.find((i) => i.id === id);
    if (isPaid(inv)) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    const eligibleIds = invoices.filter((inv) => !isPaid(inv)).map((inv) => inv.id);
    const allChecked = eligibleIds.every((id) => selectedIds.includes(id));
    if (allChecked) {
      setSelectedIds((prev) => prev.filter((k) => !eligibleIds.includes(k)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...eligibleIds])]);
    }
  };

  const getClientName = (inv) => {
    const c = inv.client;
    if (!c) return "—";
    if (c.type?.toLowerCase() === "company") return c.company_name || "—";
    if (c.primary_user)
      return `${c.primary_user.first_name} ${c.primary_user.last_name}`.trim();
    return "—";
  };

  const getCurrency = (inv) =>
    inv.flatRateReceivable?.currency?.currency?.code ||
    inv.unitBasedReceivable?.currency?.currency?.code ||
    "—";

  // Mirrors the table columns below, minus the checkbox and action columns.
  const EXPORT_COLUMNS = [
    { header: "Code", value: (inv) => inv.invoice_code || `CI${inv.id}` },
    {
      header: "Linked Payments",
      value: (inv) =>
        (inv.payments || [])
          .map((pay) => pay.payment_code || `CP${pay.id}`)
          .join(", "),
    },
    { header: "Client", value: (inv) => getClientName(inv) },
    {
      header: "Payment Coverage",
      value: (inv) => (isFullyWrittenOff(inv) ? "" : inv.coverage_by_payments || "Not covered"),
    },
    {
      header: "Total",
      value: (inv) =>
        parseFloat(inv.total || 0) > 0 ? parseFloat(inv.total).toFixed(2) : "",
    },
    { header: "Write Off Amount", value: (inv) => parseFloat(inv.write_off_amount || 0).toFixed(2) },
    { header: "Paid Amount", value: (inv) => parseFloat(inv.paid_amount || 0).toFixed(2) },
    { header: "Due Amount", value: (inv) => parseFloat(inv.due_amount || 0).toFixed(2) },
    { header: "Currency", value: (inv) => getCurrency(inv) },
    { header: "Issued At", value: (inv) => fmtISO(inv.issued_at) },
    { header: "Last Sent At", value: (inv) => fmtDateTime(inv.last_sent_at) },
    { header: "Due At", value: (inv) => fmtDateTime(inv.due_at) },
  ];

  // The table is paginated, so the export pulls every row rather than the page
  // currently on screen.
  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.get("/project-finances/client-invoices", {
        params: { page: 1, limit: 100000 },
        withCredentials: true,
      });
      exportTable("client-invoices", EXPORT_COLUMNS, res.data.data || []);
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Could not export the table.");
    } finally {
      setExporting(false);
    }
  };

  const [coverLoading, setCoverLoading] = useState(false);

  const handleCoverByPayments = async () => {
    if (selectedIds.length === 0) return;
    setCoverLoading(true);
    const count = selectedIds.length;
    try {
      await Promise.all(
        selectedIds.map((invoiceId) =>
          api.post(
            "/project-finances/client-payments",
            { linked_invoice_id: invoiceId },
            { withCredentials: true }
          )
        )
      );
      setSelectedIds([]);
      toast.success(`${count} payment(s) created successfully.`);
      fetchInvoices(meta.page);
    } catch (err) {
      console.error("Cover by payments failed:", err);
      toast.error("Failed to create one or more payments.");
    } finally {
      setCoverLoading(false);
    }
  };

  const eligibleInvoices = invoices.filter((inv) => !isPaid(inv));
  const allSelected =
    eligibleInvoices.length > 0 && eligibleInvoices.every((inv) => selectedIds.includes(inv.id));
  const someSelected = invoices.some((inv) => selectedIds.includes(inv.id));

  const totalPages = Math.ceil(meta.total / meta.limit);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Client Invoices
        </h1>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="flex items-center gap-2 text-xs text-gray-500">
            New client invoices can be created on the{" "}
            <Link to="/client/receivables" className="text-brand-600 hover:underline">
              Receivables
            </Link>{" "}
            page.
          </span>
          {!readOnly && (
            <>
              <button
                onClick={handleCoverByPayments}
                disabled={selectedIds.length === 0 || coverLoading}
                className={btn("primary", "sm")}
              >
                {coverLoading ? "Creating..." : "Cover (selection) by payments"}
              </button>
              <span className="text-gray-300">|</span>
              <button className={btn("primary", "sm")}>
                Cover (filter results) by payments
              </button>
            </>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className={btn("secondary", "sm")}
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{meta.total.toLocaleString()}</span> items.
            {selectedIds.length > 0 && (
              <span className="ml-3 text-brand-600 font-medium">
                {selectedIds.length} selected
              </span>
            )}
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1400px]">
                <colgroup>
                  {COL_WIDTHS.map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>

                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    <th className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={handleToggleAll}
                        className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                      />
                    </th>
                    {HEADERS.slice(1).map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No invoices found.
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv, index) => {
                      const isSelected = selectedIds.includes(inv.id);
                      const alreadyPaid = isPaid(inv);
                      const days = daysFromNow(inv.due_at);
                      const coverage = inv.coverage_by_payments || "Not covered";
                      const coverageCls =
                        COVERAGE_BADGE[coverage] || COVERAGE_BADGE["Not covered"];
                      const rowBg = alreadyPaid
                        ? "bg-gray-100 opacity-70"
                        : isSelected
                        ? "bg-brand-50"
                        : index % 2 === 0
                        ? "bg-gray-50"
                        : "bg-white";

                      return (
                        <tr
                          key={inv.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={alreadyPaid}
                              onChange={() => handleToggle(inv.id)}
                              className="w-3.5 h-3.5 accent-brand-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </td>

                          {/* Code */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/client/invoice/${inv.id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              {inv.invoice_code || `CI${inv.id}`}
                            </Link>
                          </td>

                          {/* Linked payments */}
                          <td className="px-3 py-2 text-xs text-center">
                            {inv.payments && inv.payments.length > 0 ? (
                              <div className="flex flex-wrap gap-1 justify-center">
                                {inv.payments.map((pay) => (
                                  <Link
                                    key={pay.id}
                                    to={`/client/payment/${pay.id}`}
                                    className="text-brand-600 hover:text-brand-800 underline font-medium whitespace-nowrap"
                                  >
                                    {pay.payment_code || `CP${pay.id}`}
                                  </Link>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          {/* Client */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            {inv.client?.id ? (
                              <Link
                                to={`/clients/${inv.client.id}`}
                                className="text-brand-600 hover:text-brand-800 underline font-medium"
                              >
                                {getClientName(inv)}
                              </Link>
                            ) : (
                              getClientName(inv)
                            )}
                          </td>

                          {/* Coverage */}
                          <td className={`px-3 py-2 text-xs text-center whitespace-nowrap ${isFullyWrittenOff(inv) ? "text-gray-400" : coverageCls}`}>
                            {isFullyWrittenOff(inv) ? "—" : coverage}
                          </td>

                          {/* Total */}
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                            {parseFloat(inv.total || 0) > 0
                              ? parseFloat(inv.total).toFixed(2)
                              : "—"}
                          </td>

                          {/* Write off amount */}
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {parseFloat(inv.write_off_amount || 0).toFixed(2)}
                          </td>

                          {/* Paid amount */}
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {parseFloat(inv.paid_amount || 0).toFixed(2)}
                          </td>

                          {/* Due amount */}
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">
                            {parseFloat(inv.due_amount || 0).toFixed(2)}
                          </td>

                          {/* Currency */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {getCurrency(inv)}
                          </td>

                          {/* Issued at */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                            {fmtISO(inv.issued_at)}
                          </td>

                          {/* Last sent at */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                            {fmtDateTime(inv.last_sent_at)}
                          </td>

                          {/* Due at */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            <div>
                              <span className="text-gray-500">{fmtDateTime(inv.due_at)}</span>
                              {days !== null && (
                                <span
                                  className={`block text-[10px] font-medium ${
                                    days < 0 ? "text-danger-500" : "text-amber-500"
                                  }`}
                                >
                                  {days < 0
                                    ? `${Math.abs(days)} days overdue`
                                    : `in ${days} days`}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Action */}
                          <td className="px-3 py-2 text-center">
                            <Download
                              className="w-4 h-4 text-gray-400 hover:text-brand-600 cursor-pointer transition-colors mx-auto"
                              title="Download as PDF"
                              onClick={() => toast.error("Download as PDF not yet implemented")}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 pt-2">
              <button
                disabled={meta.page <= 1}
                onClick={() => fetchInvoices(meta.page - 1)}
                className={btn("secondary", "sm")}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {meta.page} of {totalPages}
              </span>
              <button
                disabled={meta.page >= totalPages}
                onClick={() => fetchInvoices(meta.page + 1)}
                className={btn("secondary", "sm")}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClientInvoicesPage;
